import { useEffect, useState } from "react";

import useInterval from "hooks/useInterval";
import styles from "./dateCountdown.module.scss";

interface TimeUnits {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

export interface DateCountdownProps {
  date: string;
  onFinish?: () => void;
}

export default function DateCountdown(props: DateCountdownProps): JSX.Element | null {
  const { date, onFinish } = props;
  const [countdownDate, setCountdownDate] = useState<number | undefined>();
  const [countdown, setCountdown] = useState<TimeUnits | undefined>();
  const shouldStop = countdown ? countdown.days + countdown.hours + countdown.minutes + countdown.seconds <= 0 : false;

  function parseTime(dateToParse: number): TimeUnits {
    // Get today's date and time
    const now = new Date().getTime();

    // Find the distance between now and the count down date
    const distance = dateToParse - now;

    // Time calculations for days, hours, minutes and seconds
    const days = Math.floor(distance / (1000 * 60 * 60 * 24));
    const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((distance % (1000 * 60)) / 1000);

    return { days, hours, minutes, seconds };
  }

  useEffect(() => {
    setCountdown(parseTime(new Date(date).getTime()));
    setCountdownDate(new Date(date).getTime());
  }, [date]);

  useEffect(() => {
    if (countdown && shouldStop && onFinish) {
      onFinish();
    }
  }, [countdown]);

  useInterval(
    () => {
      if (countdownDate) {
        setCountdown(parseTime(countdownDate));
      }
    },
    shouldStop ? null : 1000
  );

  function renderCountdown(currentCountdown: TimeUnits) {
    return (
      <div>
        <span className={styles.quantity}>{currentCountdown.days}</span>
        <span className={styles.unit}>d</span>
        <span className={styles.quantity}>{currentCountdown.hours}</span>
        <span className={styles.unit}>h</span>
        <span className={styles.quantity}>{currentCountdown.minutes}</span>
        <span className={styles.unit}>m</span>
        <span className={styles.quantity}>{currentCountdown.seconds}</span>
        <span className={styles.unit}>s</span>
      </div>
    );
  }

  return countdown && !shouldStop
    ? renderCountdown(countdown)
    : renderCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0 });
}
